import React, { useRef, useState } from "react";
import { Box, Button, IconButton, LinearProgress, Stack, Typography } from "@mui/material";
import {
  SkipPrevious,
  SkipNext,
  PlayCircleFilledWhiteOutlined,
  PauseCircleOutlineOutlined,
} from "@mui/icons-material";
import { useApiFind } from "@cdv/jazz-ui";
import { Audio, FindAudiosDocument } from "../../api";
import AppBar from "../ui/AppBar";

export default function Player() {
  const { data = [] } = useApiFind<Audio>({
    query: FindAudiosDocument,
  });

  const audio = useRef<HTMLAudioElement>(null);
  const [current, setCurrent] = useState<Audio | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const index = data.findIndex((song) => song.id === current?.id);
  const previous = index > 0 ? data[index - 1] : null;
  const next = index >= 0 && index < data.length - 1 ? data[index + 1] : null;

  const play = async (song: Audio) => {
    if (!audio.current) return;

    if (current?.id === song.id) {
      playing ? audio.current.pause() : await audio.current.play();
      setPlaying(!playing);
      return;
    }

    audio.current.src = `/api-audio/${song.entry.id}`;
    audio.current.load();
    await audio.current.play();

    setCurrent(song);
    setPlaying(true);
    setProgress(0);
  };

  const seek = (e: React.MouseEvent) => {
    if (!audio.current || !duration) return;

    const { left, width } = e.currentTarget.getBoundingClientRect();
    audio.current.currentTime = ((e.clientX - left) / width) * duration;
  };

  const back = () => {
    audio.current?.pause();
    setCurrent(null);
    setPlaying(false);
    setProgress(0);
    setDuration(0);
  };

  const iconSx = (enabled: boolean) => ({
    fontSize: 49,
    color: enabled ? "#1976D2" : "#BDBDBD",
  });

  return (
    <Box>
      <AppBar title={current?.name || "Audio"} {...(playing && { onBack: back })} />
      <Stack sx={{ justifyContent: "center", minHeight: "78vh", mt: "0 !important" }} spacing={2}>
        {current ? (
          <Stack sx={{ gap: 2 }}>
            <img
              src={current.image?.id ? `/obrazky/${current.image.id}` : "/assets/audio.jpg"}
              style={{ height: "calc(100vh - 313px)", objectFit: "cover" }}
            />

            <Box sx={{ background: "white", py: "26px", px: "16px" }}>
              <Box onClick={seek} sx={{ cursor: "pointer" }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    bgcolor: "#dbeafe",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 999,
                      background: "#1976D2",
                    },
                  }}
                />
              </Box>

              <Stack
                direction="row"
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 2,
                }}
              >
                <IconButton disabled={!previous} onClick={() => previous && play(previous)}>
                  <SkipPrevious sx={iconSx(!!previous)} />
                </IconButton>

                <IconButton onClick={() => play(current)}>
                  {playing ? (
                    <PauseCircleOutlineOutlined sx={iconSx(true)} />
                  ) : (
                    <PlayCircleFilledWhiteOutlined sx={iconSx(true)} />
                  )}
                </IconButton>

                <IconButton disabled={!next} onClick={() => next && play(next)}>
                  <SkipNext sx={iconSx(!!next)} />
                </IconButton>
              </Stack>
            </Box>
          </Stack>
        ) : (
          <>
            <Stack sx={{ alignItems: "center" }}>
              <img src="/assets/audio.svg" style={{ height: 216 }} />
            </Stack>

            <Typography sx={{ textAlign: "center" }}>
              Zařaď volnoběh. <br />
              Nahrávky, které tě navedou <br />
              do klidného pruhu.
            </Typography>

            <Stack
              spacing={2}
              sx={{
                alignItems: "center",
                mt: 7,
              }}
            >
              {data.map((song) => (
                <Button
                  key={song.id}
                  variant="contained"
                  size="large"
                  sx={{ width: "211px" }}
                  fullWidth
                  onClick={() => play(song)}
                >
                  {song.name}
                </Button>
              ))}
            </Stack>
          </>
        )}

        <audio
          ref={audio}
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
          onTimeUpdate={(e) => {
            const { currentTime, duration } = e.currentTarget;
            if (duration) setProgress((currentTime / duration) * 100);
          }}
          onEnded={() => next && play(next)}
        />
      </Stack>
    </Box>
  );
}
